import React, { useState, useEffect, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Zap } from 'lucide-react';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

import { calculateVariation, makeRenderVariation } from '../utils/variationUtils';
// Palette au niveau module : identité stable, exploitable par les useMemo
const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

/**
 * GA4EventsChart - Composant pour afficher les événements Google Analytics 4
 * Affiche un graphique en barres horizontales et un tableau détaillé
 */
const GA4EventsChart = ({
  events = [],
  previousEvents = [],
  compareEnabled = false,
  formatNumber,
  loading = false,
  totalSessions = 0,
  previousTotalSessions = 0,
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'ga4_events'
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'eventCount', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;



  // Fonction pour afficher une variation
  const renderVariation = makeRenderVariation({ fontSize: '11px', format: (v) => formatNumber(v, 1) });

  // Mapping des noms d'événements GA4 vers des noms français
  const eventNameMapping = {
    'session_start': 'Début de session',
    'first_visit': 'Première visite',
    'page_view': 'Page vue',
    'scroll': 'Défilement',
    'click': 'Clic',
    'view_item': 'Vue produit',
    'view_item_list': 'Vue liste produits',
    'select_item': 'Sélection produit',
    'add_to_cart': 'Ajout panier',
    'remove_from_cart': 'Retrait panier',
    'begin_checkout': 'Début paiement',
    'add_payment_info': 'Info paiement',
    'add_shipping_info': 'Info livraison',
    'purchase': 'Achat',
    'refund': 'Remboursement',
    'view_promotion': 'Vue promotion',
    'select_promotion': 'Sélection promotion',
    'file_download': 'Téléchargement fichier',
    'video_start': 'Lecture vidéo',
    'video_progress': 'Progression vidéo',
    'video_complete': 'Vidéo terminée',
    'form_start': 'Début formulaire',
    'form_submit': 'Soumission formulaire',
    'user_engagement': 'Engagement utilisateur',
    'search': 'Recherche',
    'generate_lead': 'Génération lead',
    'sign_up': 'Inscription',
    'login': 'Connexion'
  };

  // Normaliser les données des événements
  const eventData = useMemo(() => {
    if (!events || !Array.isArray(events) || events.length === 0) {
      return [];
    }

    return events.map(event => {
      const rawName = event.eventName || 'Unknown';
      const name = eventNameMapping[rawName] || rawName;

      return {
        name,
        rawName,
        eventCount: event.eventCount || 0,
        users: event.users || 0,
        eventValue: event.eventValue || 0,
        eventsPerSession: event.eventsPerSession || 0
      };
    }).filter(e => e.eventCount > 0);
  }, [events]);

  // Calcul des totaux
  const totals = useMemo(() => {
    if (!eventData.length) return {};

    return eventData.reduce((acc, event) => ({
      eventCount: acc.eventCount + (event.eventCount || 0),
      users: acc.users + (event.users || 0),
      eventValue: acc.eventValue + (event.eventValue || 0)
    }), { eventCount: 0, users: 0, eventValue: 0 });
  }, [eventData]);

  // Calcul des totaux de la période précédente pour comparaison
  const previousTotals = useMemo(() => {
    if (!compareEnabled || !previousEvents || !previousEvents.length) return null;

    return previousEvents.reduce((acc, event) => ({
      eventCount: acc.eventCount + (event.eventCount || 0),
      users: acc.users + (event.users || 0),
      eventValue: acc.eventValue + (event.eventValue || 0)
    }), { eventCount: 0, users: 0, eventValue: 0 });
  }, [compareEnabled, previousEvents]);

  // Données triées
  const sortedEvents = useMemo(() => {
    if (!eventData.length) return [];

    const sorted = [...eventData];
    sorted.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'name') {
        return sortConfig.direction === 'asc'
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      }

      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;

      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  }, [eventData, sortConfig]);

  // Couleur figée par événement, sur l'ordre brut : le graphique est classé par fréquence
  // et le tableau suit le tri de l'utilisateur, les deux doivent rester d'accord
  const eventColorMap = useMemo(() => {
    const map = {};
    eventData.forEach((event, index) => {
      map[event.name] = COLORS[index % COLORS.length];
    });
    return map;
  }, [eventData]);

  // Pagination
  const totalPages = Math.ceil(sortedEvents.length / ITEMS_PER_PAGE);

  // Revenir à la première page si les données raccourcissent la liste
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const paginatedEvents = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedEvents.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [sortedEvents, currentPage]);

  // Top 10 pour le graphique
  const chartData = useMemo(() => {
    // Toujours les 10 plus fréquents, indépendamment du tri choisi pour le tableau
    return [...eventData]
      .sort((a, b) => (Number(b.eventCount) || 0) - (Number(a.eventCount) || 0))
      .slice(0, 10)
      .map(event => ({
      name: event.name.length > 20 ? event.name.substring(0, 20) + '...' : event.name,
      fullName: event.name,
      eventCount: event.eventCount,
      users: event.users
    }));
  }, [eventData]);

  // Handler de tri
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Ne pas afficher si pas de données ou en chargement
  if (loading) {
    return (
      <div className="device-conversions-section">
        <div className="section-header">
          <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Zap size={18} />
            Événements
          </h4>
        </div>
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          Chargement des données...
        </div>
      </div>
    );
  }

  if (!eventData.length) {
    return null;
  }

  return (
    <>
      <div className="section-header">
        <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Zap size={18} />
          Événements
        </h4>
        {toggleClientModuleVisibility ? (
          <ModuleVisibilityToggle
            isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
            onToggle={toggleClientModuleVisibility}
            userRole={userRole}
            moduleId={moduleId}
            title="Événements GA4"
          />
        ) : (
          <button
            onClick={() => setIsVisible(!isVisible)}
            className="section-toggle-btn"
            title={isVisible ? "Masquer la section" : "Afficher la section"}
          >
            {isVisible ? '−' : '+'}
          </button>
        )}
      </div>

      {isVisible && (
        <>
          {/* Graphique en barres horizontales - Top 10 */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                <h5 style={{ margin: '0 0 15px 0', color: '#374151', fontSize: '14px' }}>Top 10 événements</h5>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={chartData}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      tick={{ fontSize: 11 }}
                      width={100}
                    />
                    <Tooltip
                      formatter={(value, name) => [formatNumber(value), name === 'eventCount' ? 'Événements' : 'Utilisateurs']}
                      labelFormatter={(label) => chartData.find(d => d.name === label)?.fullName || label}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Bar dataKey="eventCount" name="Événements" radius={[0, 4, 4, 0]}>
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={eventColorMap[entry.fullName] || COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
          </div>

          {/* Tableau détaillé */}
          <div className="device-table-wrapper" style={{ marginTop: '0', paddingTop: '0' }}>
            <div className="device-summary-table" style={{ marginTop: '0', paddingTop: '0' }}>
              <table
                className="devices-table"
                style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'collapse' }}
              >
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')} className="sortable">
                      Événement {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('eventCount')} className="sortable numeric">
                      Nombre {sortConfig.key === 'eventCount' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('users')} className="sortable numeric">
                      Utilisateurs {sortConfig.key === 'users' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('eventsPerSession')} className="sortable numeric">
                      Par session {sortConfig.key === 'eventsPerSession' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('eventValue')} className="sortable numeric">
                      Valeur {sortConfig.key === 'eventValue' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedEvents.map((event, index) => {
                    const globalIndex = (currentPage - 1) * ITEMS_PER_PAGE + index;
                    return (
                      <tr key={index}>
                        <td className="device-name" style={{ display: 'flex', alignItems: 'center' }}>
                          <span
                            style={{
                              backgroundColor: eventColorMap[event.name] || COLORS[globalIndex % COLORS.length],
                              width: '10px',
                              minWidth: '10px',
                              height: '10px',
                              minHeight: '10px',
                              borderRadius: '50%',
                              display: 'inline-block',
                              marginRight: '8px',
                              flexShrink: 0
                            }}
                          ></span>
                          {event.name}
                          {event.name !== event.rawName && (
                            <span style={{ color: '#9ca3af', fontSize: '0.85em', marginLeft: '6px' }}>
                              ({event.rawName})
                            </span>
                          )}
                        </td>
                        <td className="numeric">
                          {formatNumber(event.eventCount)}
                          {compareEnabled && previousEvents && (() => {
                            const prev = previousEvents.find(p => p.eventName === event.rawName);
                            if (prev) return renderVariation(calculateVariation(event.eventCount, prev.eventCount || 0));
                            return null;
                          })()}
                        </td>
                        <td className="numeric">
                          {formatNumber(event.users)}
                          {compareEnabled && previousEvents && (() => {
                            const prev = previousEvents.find(p => p.eventName === event.rawName);
                            if (prev) return renderVariation(calculateVariation(event.users, prev.users || 0));
                            return null;
                          })()}
                        </td>
                        <td className="numeric">
                          {formatNumber(event.eventsPerSession, 2)}
                          {compareEnabled && previousEvents && (() => {
                            const prev = previousEvents.find(p => p.eventName === event.rawName);
                            if (prev) return renderVariation(calculateVariation(event.eventsPerSession, prev.eventsPerSession || 0));
                            return null;
                          })()}
                        </td>
                        <td className="numeric">
                          {formatNumber(event.eventValue, 2)}€
                          {compareEnabled && previousEvents && (() => {
                            const prev = previousEvents.find(p => p.eventName === event.rawName);
                            if (prev) return renderVariation(calculateVariation(event.eventValue, prev.eventValue || 0));
                            return null;
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>

                <tfoot>
                  <tr className="total-row">
                    <td className="device-name"><strong>TOTAL</strong></td>
                    <td className="numeric">
                      <strong>{formatNumber(totals.eventCount)}</strong>
                      {previousTotals && renderVariation(calculateVariation(totals.eventCount, previousTotals.eventCount))}
                    </td>
                    <td className="numeric">
                      <strong>{formatNumber(totals.users)}</strong>
                      {previousTotals && renderVariation(calculateVariation(totals.users, previousTotals.users))}
                    </td>
                    <td className="numeric">
                      <strong>{totalSessions > 0 ? formatNumber(totals.eventCount / totalSessions, 2) : formatNumber(0, 2)}</strong>
                      {previousTotals && renderVariation(calculateVariation(
                        totalSessions > 0 ? totals.eventCount / totalSessions : 0,
                        previousTotalSessions > 0 ? previousTotals.eventCount / previousTotalSessions : 0
                      ))}
                    </td>
                    <td className="numeric">
                      <strong>{formatNumber(totals.eventValue, 2)}€</strong>
                      {previousTotals && renderVariation(calculateVariation(totals.eventValue, previousTotals.eventValue))}
                    </td>
                  </tr>
                </tfoot>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <div className="pagination-info">
                    Affichage {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, sortedEvents.length)} sur {sortedEvents.length} événements
                  </div>
                  <div className="pagination-buttons">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="pagination-btn"
                    >
                      Précédent
                    </button>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="pagination-btn"
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default GA4EventsChart;
